import React from "react";
import {DataNode} from "rc-tree/lib/interface";
import {connect} from "react-redux";

import {ActiveFacilities} from "../../store/reporting/reporting.common";
import {AppState, select} from "../../store";
import {DropdownTreeSelect} from "../dropdown-tree-select";

import {prepareFacilitiesTreeForDisplay} from "./helpers";
import {Wrapper, FacilitySelectWrapper} from "./styles";
import {ThinScrollbar} from "@reside/ui";

type Props = Readonly<
  {
    hasOnlyOne?: boolean;
    hideTrigger?: boolean;
    searchPlaceholder?: string;
    triggerLabel?: string;
    autoComplete?: string;
    withApplyFiltersButton?: boolean;
    withoutFacilityName?: boolean;
  } & StateProps &
    DispatchProps
>;

export const FacilityDropdownTreeSelectRenderer = ({
  activeFacilitiesTreeKeys,
  facilitiesTree,
  hasOnlyOne,
  hideTrigger = false,
  searchPlaceholder = "Search by organization or facility",
  setActiveFacilities,
  triggerLabel = "Facilities",
  autoComplete,
  withApplyFiltersButton = false,
  withoutFacilityName = false,
}: Props) => (
  <Wrapper>
    <ThinScrollbar>
      <DropdownTreeSelect
        checkedKeys={activeFacilitiesTreeKeys}
        EmptySearch={() => <span>No matching organization or facility</span>}
        hideTrigger={hideTrigger}
        hasOnlyOne={hasOnlyOne}
        placement="bottom-start"
        searchPlaceholder={searchPlaceholder}
        treeData={facilitiesTree}
        triggerLabel={triggerLabel}
        onCheck={(checkedKeys, checkedLeafKeys) => {
          setActiveFacilities({
            checkedKeys,
            checkedLeafKeys,
          });
        }}
        autoComplete={autoComplete}
        withApplyFiltersButton={withApplyFiltersButton}
        withoutFacilityName={withoutFacilityName}
      />
    </ThinScrollbar>
  </Wrapper>
);

const Connected = ({
  activeFacilitiesTreeKeys,
  facilitiesTree,
  setActiveFacilities,
  withoutFacilityName = false,
}: StateProps & DispatchProps) => {
  const {modifiedFacilityTree, hasSingleFacility, hasSingleOrganization} =
    prepareFacilitiesTreeForDisplay(facilitiesTree);

  const singleFacility = activeFacilitiesTreeKeys?.length === 1 || hasSingleFacility;

  return (
    modifiedFacilityTree?.length > 0 && (
      <FacilitySelectWrapper hasSingleOrganization={hasSingleOrganization}>
        <FacilityDropdownTreeSelectRenderer
          activeFacilitiesTreeKeys={activeFacilitiesTreeKeys}
          facilitiesTree={modifiedFacilityTree}
          hideTrigger={hasSingleFacility}
          setActiveFacilities={setActiveFacilities}
          triggerLabel={singleFacility ? null : "Facilities"}
          withApplyFiltersButton
          withoutFacilityName={singleFacility ? false : withoutFacilityName} // if the user has a single facility or a single facility is selected in the filter show it's name
        />
      </FacilitySelectWrapper>
    )
  );
};

type StateProps = {
  activeFacilitiesTreeKeys: ReadonlyArray<string>;
  facilitiesTree: ReadonlyArray<DataNode>;
  withoutFacilityName?: boolean;
};

const mapStateToProps = (state: AppState) => ({
  facilitiesTree: select.adminSession.facilitiesTree(state),
  activeFacilitiesTreeKeys: select.adminSession.activeFacilitiesTreeKeys(state),
});

type DispatchProps = {
  setActiveFacilities: (data: ActiveFacilities) => void;
};

const mapDispatchToProps = (dispatch: any) => ({
  setActiveFacilities: (payload: ActiveFacilities) => {
    dispatch.adminSession.setActiveFacilities(payload);
  },
});

export const FacilityDropdownTreeSelect = connect(
  mapStateToProps,
  mapDispatchToProps,
)(Connected);
